import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, useScroll, useTransform, useInView, useSpring } from "framer-motion";
import { 
  ChevronRight, CheckCircle2, Users, Clock, TrendingUp, ArrowRight, 
  Home, Shield, Target, Award, Heart, Key, Star, ShieldCheck, BadgeCheck, 
  UserPlus, X, Building2, Handshake, BarChart3, DollarSign, Briefcase,
  Phone, Mail, MapPin, Upload, FileText, Menu
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import CFIConversationalSurvey from "@/components/CFIConversationalSurvey";
import { CFI_OFFICE_OPTIONS } from "@/constants/locations";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

// Animated Counter Component
const AnimatedCounter = ({ target, suffix = "", duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(target.replace(/[^0-9]/g, ''));
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += Math.ceil(end / 50);
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [isInView, target, duration]);
  
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// Section wrapper with scroll animation
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: "easeOut" } }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// CFI Brand Colors
const CFI_GREEN = "#357c2a";
const CFI_GREEN_LIGHT = "#84c77f";
const CFI_GREEN_DARK = "#265324";

// Logo URLs - using the correct filename
const CFI_LOGO = "/images/cfi-logo-main.png";

const INDUSTRIES = [
  "Home Builders",
  "Real Estate Agencies",
  "Mortgage Lenders",
  "Auto Dealerships",
  "Financial Advisors",
  "Insurance Agencies",
  "Property Management",
  "Other",
];

const COMPANY_SIZES = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "500+ employees",
];

// Error Boundary for third-party widgets
class CFIWidgetErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.warn("Review widget error caught:", err.message); }
  render() { return this.state.hasError ? null : this.props.children; }
}

// CFI Review Widget Component - deferred loading to prevent scroll hijacking
const CFIReviewWidget = () => {
  const widgetRef = useRef(null);
  const [showWidget, setShowWidget] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    // Suppress postMessage errors from third-party widget scripts
    const handleError = (event) => {
      if (event.message && event.message.includes("postMessage")) {
        event.preventDefault();
        console.warn("Suppressed third-party postMessage error");
      }
    };
    window.addEventListener("error", handleError);

    const timer = setTimeout(() => {
      setShowWidget(true);
    }, 1500);

    const script = document.createElement('script');
    script.src = 'https://reputationhub.site/reputation/assets/review-widget.js';
    script.type = 'text/javascript';
    script.async = true;
    script.onerror = () => setLoadFailed(true);
    document.head.appendChild(script);

    return () => {
      window.removeEventListener("error", handleError);
      clearTimeout(timer);
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  if (loadFailed) return null;

  return (
    <CFIWidgetErrorBoundary>
      <div ref={widgetRef} className="w-full" style={{ minHeight: '400px' }}>
        {showWidget && (
          <iframe 
            className="lc_reviews_widget" 
            src="https://reputationhub.site/reputation/widgets/review_widget/niCJVilKzKOFQ9HmHKz2?widgetId=697947aac90b10e699b39f5b" 
            frameBorder="0" 
            scrolling="no" 
            style={{ minWidth: '100%', width: '100%', minHeight: '400px' }}
            title="Credit Firm Inc Reviews"
            tabIndex="-1"
            loading="lazy"
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        )}
      </div>
    </CFIWidgetErrorBoundary>
  );
};




// Secondary Lead Types
const SECONDARY_LEAD_TYPES = [
  { value: "single", label: "Single" },
  { value: "joint", label: "Joint" },
  { value: "mortgage_affiliate", label: "Mortgage Affiliate Referred" },
  { value: "realtor_affiliate", label: "Realtor Affiliate Referred" },
  { value: "other", label: "Other Type of Referral" },
];

// Timezones
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Phoenix", label: "Arizona Time (AZ)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
];

const CFILandingPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showPartnerInquiryModal, setShowPartnerInquiryModal] = useState(false);
  const [submittingClient, setSubmittingClient] = useState(false);
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [clientFormData, setClientFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    timezone: "America/Chicago",
    office_location: "",
    betenbough_sales_rep: "",
    betenbough_affiliate_2: "",
    betenbough_special_notes_or_credit_strategy: "",
    internal_team_spanish: false,
    internal_team_new_lead: false,
    internal_team_special_affiliate: false,
    internal_team_direct_counselor: false,
    // Joint applicant
    has_joint: false,
    joint_first_name: "",
    joint_last_name: "",
    joint_email: "",
    joint_phone: "",
    joint_dob: "",
    // Internal use only
    betenbough_assigned_credit_counselor: "",
    corporate_manager: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [partnerInquiryData, setPartnerInquiryData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    industry: "",
    company_size: "",
    message: "",
  });

  // Ensure page starts at top - aggressive scroll control to prevent iframe focus stealing
  useEffect(() => {
    // Disable smooth scroll temporarily
    document.documentElement.style.scrollBehavior = 'auto';
    
    // Immediate scroll
    window.scrollTo(0, 0);
    
    // Use requestAnimationFrame for reliable scroll after render
    const raf = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    
    // Delayed scroll to override any lazy-loaded content or iframes that might steal focus
    const scrollTimer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    // Additional fallback for iframe load events
    const fallbackTimer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 500);
    
    // Restore smooth scroll after ensuring we're at top
    const restoreTimer = setTimeout(() => {
      document.documentElement.style.scrollBehavior = '';
    }, 1000);
    
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(scrollTimer);
      clearTimeout(fallbackTimer);
      clearTimeout(restoreTimer);
    };
  }, []);

  // Fetch affiliates and counselors when modal opens
  useEffect(() => {
    if (showAddClientModal) {
      fetchAffiliates();
      fetchCounselors();
    }
  }, [showAddClientModal]);

  const fetchAffiliates = async () => {
    try {
      const response = await fetch(`${API}/lookup/affiliates`);
      if (response.ok) {
        const data = await response.json();
        setAffiliates(data.affiliates || []);
      }
    } catch (error) {
      console.error("Error fetching affiliates:", error);
    }
  };

  const fetchCounselors = async () => {
    try {
      const response = await fetch(`${API}/lookup/counselors`);
      if (response.ok) {
        const data = await response.json();
        setCounselors(data.counselors || []);
      }
    } catch (error) {
      console.error("Error fetching counselors:", error);
    }
  };

  const handleClientFormChange = (field, value) => {
    setClientFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePartnerInquiryChange = (field, value) => {
    setPartnerInquiryData(prev => ({ ...prev, [field]: value }));
  };

  const handleClientFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientFormData.first_name || !clientFormData.last_name) {
      toast.error("First name and last name are required");
      return;
    }

    setSubmittingClient(true);
    try {
      // Build internal use only string - default to "New Lead Team" if nothing selected
      const internalUseTeams = [];
      if (clientFormData.internal_team_new_lead) internalUseTeams.push("New Lead Team");
      if (clientFormData.internal_team_spanish) internalUseTeams.push("Spanish Team");
      if (clientFormData.internal_team_special_affiliate) internalUseTeams.push("Special Affiliate Lead Team");
      if (clientFormData.internal_team_direct_counselor) internalUseTeams.push("Direct to Counselor");

      const submitData = {
        ...clientFormData,
        internal_use_only: internalUseTeams.length > 0 ? internalUseTeams.join(", ") : "New Lead Team",
        source: "cfi_partner_landing",
        secondary_type: clientFormData.has_joint ? "Joint" : "Single",
        has_joint_client: clientFormData.has_joint,
        lead_status_amp_stages: "New Lead",
        corporate_manager: clientFormData.corporate_manager,
        assigned_counselor: clientFormData.betenbough_assigned_credit_counselor,
      };

      // Use FormData for file upload support via the public referrals endpoint
      const formData = new FormData();
      formData.append("data", JSON.stringify(submitData));
      
      // Attach files if any
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          formData.append("files", file);
        });
      }

      const response = await fetch(`${API}/referrals/submit-with-files`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json().catch(() => null);
      if (response.ok || (result && result.success)) {
        toast.success("Client added successfully! Syncing to CRM...", { duration: 4000 });
        setShowAddClientModal(false);
        setClientFormData({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          timezone: "America/Chicago",
          betenbough_sales_rep: "",
          betenbough_affiliate_2: "",
          betenbough_special_notes_or_credit_strategy: "",
          internal_team_spanish: false,
          internal_team_new_lead: false,
          internal_team_special_affiliate: false,
          internal_team_direct_counselor: false,
          has_joint: false,
          joint_first_name: "",
          joint_last_name: "",
          joint_email: "",
          joint_phone: "",
          joint_dob: "",
          betenbough_assigned_credit_counselor: "",
          corporate_manager: "",
        });
        setUploadedFiles([]);
      } else if (response.status === 401) {
        toast.error("Your session has expired. Please log in again.");
      } else {
        const errorMsg = result?.detail || "Failed to add client. Please try again.";
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Connection error — please check your internet and try again.", { duration: 6000 });
    } finally {
      setSubmittingClient(false);
    }
  };

  const handlePartnerInquirySubmit = async (e) => {
    e.preventDefault();
    
    if (!partnerInquiryData.company_name || !partnerInquiryData.contact_name || !partnerInquiryData.email) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmittingInquiry(true);
    try {
      // For now, we&apos;ll show success and log the data
      // In production, this would submit to a backend endpoint
      console.log("Partner inquiry:", partnerInquiryData);
      toast.success("Thank you for your interest! Our partnership team will contact you within 24-48 hours.");
      setShowPartnerInquiryModal(false);
      setPartnerInquiryData({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        industry: "",
        company_size: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry");
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const handleSurveyComplete = (data) => {
    setIsSubmitted(true);
  };

  // Success State View
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-[#357c2a] to-[#265324] sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <img 
                src={CFI_LOGO} 
                alt="Credit Firm Inc" 
                className="h-10 sm:h-14 w-auto" 
              />
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-3">
                <Button 
                  onClick={() => setShowAddClientModal(true)}
                  className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
                  data-testid="quick-add-client-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Quick Add Client
                </Button>
                <div className="flex flex-col items-center">
                  <Link to="/login">
                    <Button 
                      variant="outline" 
                      className="border-[#84c77f] text-[#84c77f] hover:bg-[#84c77f] hover:text-[#265324]"
                      data-testid="login-btn"
                    >
                      Partner Login
                    </Button>
                  </Link>
                  <Link to="/register" className="text-xs text-[#84c77f]/80 hover:text-[#84c77f] mt-1">
                    Create Free Account
                  </Link>
                </div>
                <Link to="/login?type=client">
                  <Button 
                    className="bg-[#84c77f] text-[#265324] hover:bg-[#9ed899]"
                    data-testid="client-login-btn"
                  >
                    Client Login
                  </Button>
                </Link>
              </div>

              {/* Mobile Navigation - always visible */}
              <div className="flex md:hidden items-center gap-1.5">
                <Button 
                  onClick={() => setShowAddClientModal(true)}
                  className="bg-white/20 text-white hover:bg-white/30 border border-white/30 text-xs px-2.5 py-1.5 h-auto"
                  data-testid="success-mobile-quick-add-btn"
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                  Add Client
                </Button>
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="border-[#84c77f] text-[#84c77f] hover:bg-[#84c77f] hover:text-[#265324] text-xs px-2.5 py-1.5 h-auto"
                    data-testid="success-mobile-login-btn"
                  >
                    Partner
                  </Button>
                </Link>
                <Link to="/login?type=client">
                  <Button 
                    className="bg-[#84c77f] text-[#265324] hover:bg-[#9ed899] text-xs px-2.5 py-1.5 h-auto"
                    data-testid="success-mobile-client-login-btn"
                  >
                    Client
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Success Message */}
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="text-center animate-fade-in">
            <div className="w-16 sm:w-20 h-16 sm:h-20 bg-[#84c77f] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 sm:h-10 w-8 sm:w-10 text-[#265324]" />
            </div>
            <h1 className="text-4xl font-bold text-[#265324] mb-4">
              You&apos;re One Step Closer to Your Goals!
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
              Thank you for reaching out. Our credit counseling team will contact you 
              within 24-48 hours to start your personalized journey to becoming loan-ready.
            </p>
            
            <Card className="text-left mb-8 border-[#357c2a]/10">
              <CardHeader>
                <CardTitle className="text-lg text-[#265324]">What Happens Next?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#84c77f] rounded-full flex items-center justify-center text-sm font-semibold text-[#265324]">1</div>
                  <div>
                    <p className="font-medium text-[#265324]">Personal Review</p>
                    <p className="text-sm text-gray-600">A credit counselor reviews your information and creates a personalized plan.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#357c2a] rounded-full flex items-center justify-center text-sm font-semibold text-white">2</div>
                  <div>
                    <p className="font-medium text-[#265324]">Your Counselor Calls</p>
                    <p className="text-sm text-gray-600">You&apos;ll receive a call to discuss your goals and create an action plan together.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#265324] rounded-full flex items-center justify-center text-sm font-semibold text-white">3</div>
                  <div>
                    <p className="font-medium text-[#265324]">Start Your Journey</p>
                    <p className="text-sm text-gray-600">Begin working toward loan-ready status with expert guidance every step of the way.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-center">
              <Link to="/cfi">
                <Button 
                  variant="outline" 
                  className="border-[#357c2a] text-[#357c2a] hover:bg-[#357c2a] hover:text-white"
                  data-testid="back-to-home-btn"
                >
                  Back to Home
                </Button>
              </Link>
              <Link to="/login?type=client">
                <Button 
                  className="bg-[#357c2a] text-white hover:bg-[#265324]"
                  data-testid="go-to-portal-btn"
                >
                  Track My Progress <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Add Client Modal */}
      <Dialog open={showAddClientModal} onOpenChange={setShowAddClientModal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain touch-pan-y">
          <DialogHeader className="pb-4 border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#357c2a] to-[#265324] rounded-xl flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl text-[#265324]">Add New Client</DialogTitle>
                <p className="text-sm text-slate-500 mt-1">Enter the client&apos;s details below. Fields marked with * are required.</p>
              </div>
            </div>
          </DialogHeader>
          
          <form onSubmit={handleClientFormSubmit} className="space-y-6 pt-4">
            {/* Personal Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#265324] border-b pb-2">
                <div className="w-6 h-6 bg-[#84c77f] rounded-full flex items-center justify-center text-xs text-[#265324]">1</div>
                Personal Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="modal_first_name" className="text-slate-700">First Name *</Label>
                  <Input
                    id="modal_first_name"
                    value={clientFormData.first_name}
                    onChange={(e) => handleClientFormChange("first_name", e.target.value)}
                    placeholder="John"
                    className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                    required
                    data-testid="modal-first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="modal_last_name" className="text-slate-700">Last Name *</Label>
                  <Input
                    id="modal_last_name"
                    value={clientFormData.last_name}
                    onChange={(e) => handleClientFormChange("last_name", e.target.value)}
                    placeholder="Doe"
                    className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                    required
                    data-testid="modal-last-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="modal_email" className="text-slate-700">Email</Label>
                  <Input
                    id="modal_email"
                    type="email"
                    value={clientFormData.email}
                    onChange={(e) => handleClientFormChange("email", e.target.value)}
                    placeholder="john.doe@example.com"
                    className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                    data-testid="modal-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="modal_phone" className="text-slate-700">Phone</Label>
                  <Input
                    id="modal_phone"
                    value={clientFormData.phone}
                    onChange={(e) => handleClientFormChange("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                    data-testid="modal-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="modal_timezone" className="text-slate-700">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Time Zone
                  </Label>
                  <Select
                    value={clientFormData.timezone}
                    onValueChange={(v) => handleClientFormChange("timezone", v)}
                  >
                    <SelectTrigger className="mt-1 border-slate-200 focus:border-[#357c2a]" data-testid="modal-timezone-select">
                      <SelectValue placeholder="Select Time Zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="modal_office_location" className="text-slate-700">
                    <MapPin className="h-4 w-4 inline mr-1" />
                    Office Location
                  </Label>
                  <Select
                    value={clientFormData.office_location}
                    onValueChange={(v) => handleClientFormChange("office_location", v)}
                  >
                    <SelectTrigger className="mt-1 border-slate-200 focus:border-[#357c2a]" data-testid="modal-office-location-select">
                      <SelectValue placeholder="Select Office Location" />
                    </SelectTrigger>
                    <SelectContent>
                      {CFI_OFFICE_OPTIONS.map((loc) => (
                        <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Address - REMOVED */}

            {/* Assignment Details */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#265324] border-b pb-2 flex-1">
                  <div className="w-6 h-6 bg-[#265324] rounded-full flex items-center justify-center text-xs text-white">2</div>
                  Assignment Details
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="modal_affiliate1" className="text-slate-700">Affiliate 1 (Referral Partner)</Label>
                  <SearchableSelect
                    options={affiliates}
                    value={clientFormData.betenbough_sales_rep}
                    onValueChange={(v) => handleClientFormChange("betenbough_sales_rep", v)}
                    placeholder="Search or select affiliate..."
                    className="mt-1"
                    data-testid="modal-affiliate1-select"
                  />
                </div>
                <div>
                  <Label htmlFor="modal_affiliate2" className="text-slate-700">Affiliate 2 (Optional)</Label>
                  <SearchableSelect
                    options={affiliates}
                    value={clientFormData.betenbough_affiliate_2}
                    onValueChange={(v) => handleClientFormChange("betenbough_affiliate_2", v)}
                    placeholder="Search or select affiliate..."
                    className="mt-1"
                    data-testid="modal-affiliate2-select"
                  />
                </div>
                <div className="md:col-span-2">
                  <Link 
                    to="/register" 
                    className="text-xs text-[#357c2a] hover:text-[#84c77f] flex items-center gap-1"
                    onClick={() => setShowAddClientModal(false)}
                  >
                    Can't find your name? Create a free account here
                  </Link>
                </div>
              </div>
            </div>

            {/* Joint Applicant Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#265324] border-b pb-2">
                <div className="w-6 h-6 bg-[#84c77f] rounded-full flex items-center justify-center text-xs text-[#265324]">3</div>
                Joint Applicant
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={clientFormData.has_joint}
                    onChange={(e) => handleClientFormChange("has_joint", e.target.checked)}
                    className="rounded border-slate-300"
                    data-testid="modal-has-joint-checkbox"
                  />
                  <span className="text-sm text-slate-700">This is a joint application</span>
                </label>
              </div>
              {clientFormData.has_joint && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-2 border-l-2 border-[#84c77f]">
                  <div>
                    <Label className="text-slate-700">Joint First Name *</Label>
                    <Input
                      value={clientFormData.joint_first_name}
                      onChange={(e) => handleClientFormChange("joint_first_name", e.target.value)}
                      placeholder="Jane"
                      className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                      required
                      data-testid="modal-joint-first-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Joint Last Name *</Label>
                    <Input
                      value={clientFormData.joint_last_name}
                      onChange={(e) => handleClientFormChange("joint_last_name", e.target.value)}
                      placeholder="Doe"
                      className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                      required
                      data-testid="modal-joint-last-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Joint Email</Label>
                    <Input
                      type="email"
                      value={clientFormData.joint_email}
                      onChange={(e) => handleClientFormChange("joint_email", e.target.value)}
                      placeholder="jane.doe@example.com"
                      className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                      data-testid="modal-joint-email"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Joint Phone</Label>
                    <Input
                      value={clientFormData.joint_phone}
                      onChange={(e) => handleClientFormChange("joint_phone", e.target.value)}
                      placeholder="(555) 123-4567"
                      className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                      data-testid="modal-joint-phone"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#265324] border-b pb-2">
                <div className="w-6 h-6 bg-[#84c77f] rounded-full flex items-center justify-center text-xs text-[#265324]">4</div>
                Notes
              </div>
              <div>
                <Label htmlFor="modal_notes" className="text-slate-700">Special Notes</Label>
                <Textarea
                  id="modal_notes"
                  value={clientFormData.betenbough_special_notes_or_credit_strategy}
                  onChange={(e) => handleClientFormChange("betenbough_special_notes_or_credit_strategy", e.target.value)}
                  placeholder="Enter any special notes or credit strategy details..."
                  rows={3}
                  className="mt-1 border-slate-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
                  data-testid="modal-notes-input"
                />
              </div>
            </div>

            {/* Documents Upload */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#265324] border-b pb-2">
                <div className="w-6 h-6 bg-[#357c2a] rounded-full flex items-center justify-center text-xs text-white">5</div>
                Documents
              </div>
              <div 
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all border-slate-300 hover:border-[#357c2a]"
                onClick={() => document.getElementById('modal-file-upload')?.click()}
              >
                <input
                  id="modal-file-upload"
                  type="file"
                  multiple
                  onChange={(e) => setUploadedFiles(Array.from(e.target.files || []))}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
                />
                <Upload className="h-6 w-6 mx-auto mb-2 text-slate-400" />
                <p className="text-sm font-medium text-slate-700">
                  OPTIONAL: If Loan Officer, Please Upload Credit Report or Any File Type
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Drag & drop or click to upload • Multiple Files Allowed
                </p>
              </div>
              {uploadedFiles.length > 0 && (
                <div className="space-y-1">
                  {uploadedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                      <FileText className="h-4 w-4 text-[#357c2a]" />
                      <span className="truncate flex-1">{file.name}</span>
                      <button 
                        type="button" 
                        onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Internal Use Only */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 border-b border-amber-300 pb-2">
                <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs text-white">6</div>
                <Shield className="h-4 w-4" />
                For Internal Use Only
              </div>
              
              <div className="p-4 bg-amber-50 rounded-lg space-y-4">
                <div>
                  <Label className="text-slate-700 mb-2 block">
                    <Users className="h-4 w-4 inline mr-2" />
                    Assign to Team
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="modal_new_lead_team"
                        checked={clientFormData.internal_team_new_lead}
                        onCheckedChange={(c) => handleClientFormChange("internal_team_new_lead", c)}
                      />
                      <Label htmlFor="modal_new_lead_team" className="text-sm cursor-pointer">New Lead Team</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="modal_special_affiliate"
                        checked={clientFormData.internal_team_special_affiliate}
                        onCheckedChange={(c) => handleClientFormChange("internal_team_special_affiliate", c)}
                      />
                      <Label htmlFor="modal_special_affiliate" className="text-sm cursor-pointer">Special Affiliate Lead Team</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="modal_spanish_team"
                        checked={clientFormData.internal_team_spanish}
                        onCheckedChange={(c) => handleClientFormChange("internal_team_spanish", c)}
                      />
                      <Label htmlFor="modal_spanish_team" className="text-sm cursor-pointer">Spanish Team</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="modal_direct_counselor"
                        checked={clientFormData.internal_team_direct_counselor}
                        onCheckedChange={(c) => handleClientFormChange("internal_team_direct_counselor", c)}
                      />
                      <Label htmlFor="modal_direct_counselor" className="text-sm cursor-pointer">Direct to Counselor</Label>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Defaults to "New Lead Team" if none selected</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">Credit Counselor</Label>
                    <Select
                      value={clientFormData.betenbough_assigned_credit_counselor}
                      onValueChange={(v) => handleClientFormChange("betenbough_assigned_credit_counselor", v)}
                    >
                      <SelectTrigger className="mt-1" data-testid="modal-counselor-select">
                        <SelectValue placeholder="Select Counselor" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {counselors.length > 0 ? (
                          counselors.map((name, idx) => (
                            <SelectItem key={idx} value={name}>{name}</SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled>Loading counselors...</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700">Manager</Label>
                    <Select
                      value={clientFormData.corporate_manager}
                      onValueChange={(v) => handleClientFormChange("corporate_manager", v)}
                    >
                      <SelectTrigger className="mt-1" data-testid="modal-manager-select">
                        <SelectValue placeholder="Select Manager" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Dennis">Dennis</SelectItem>
                        <SelectItem value="Lesa">Lesa</SelectItem>
                        <SelectItem value="Gage">Gage</SelectItem>
                        <SelectItem value="Javan">Javan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddClientModal(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingClient}
                className="bg-gradient-to-r from-[#357c2a] to-[#265324] hover:from-[#2a6321] hover:to-[#1a3d18] text-white px-6"
                data-testid="modal-submit-client-btn"
              >
                {submittingClient ? "Submitting..." : "Add Client"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Partner Inquiry Modal */}
      <Dialog open={showPartnerInquiryModal} onOpenChange={setShowPartnerInquiryModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#357c2a] to-[#265324] rounded-xl flex items-center justify-center">
                <Handshake className="h-6 w-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl text-[#265324]">Become a Partner</DialogTitle>
                <p className="text-sm text-slate-500 mt-1">Tell us about your business and goals</p>
              </div>
            </div>
          </DialogHeader>
          
          <form onSubmit={handlePartnerInquirySubmit} className="space-y-6 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Company Name *</Label>
                <Input
                  value={partnerInquiryData.company_name}
                  onChange={(e) => handlePartnerInquiryChange("company_name", e.target.value)}
                  placeholder="Your Company"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-700">Contact Name *</Label>
                <Input
                  value={partnerInquiryData.contact_name}
                  onChange={(e) => handlePartnerInquiryChange("contact_name", e.target.value)}
                  placeholder="Your Name"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-700">Email *</Label>
                <Input
                  type="email"
                  value={partnerInquiryData.email}
                  onChange={(e) => handlePartnerInquiryChange("email", e.target.value)}
                  placeholder="email@company.com"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-700">Phone</Label>
                <Input
                  value={partnerInquiryData.phone}
                  onChange={(e) => handlePartnerInquiryChange("phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-700">Industry</Label>
                <Select
                  value={partnerInquiryData.industry}
                  onValueChange={(v) => handlePartnerInquiryChange("industry", v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Company Size</Label>
                <Select
                  value={partnerInquiryData.company_size}
                  onValueChange={(v) => handlePartnerInquiryChange("company_size", v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Size" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map((size) => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Tell us about your partnership goals</Label>
              <Textarea
                value={partnerInquiryData.message}
                onChange={(e) => handlePartnerInquiryChange("message", e.target.value)}
                placeholder="What are you hoping to achieve through a partnership with Credit Firm Inc?"
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowPartnerInquiryModal(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingInquiry}
                className="bg-gradient-to-r from-[#357c2a] to-[#265324] text-white px-6"
              >
                {submittingInquiry ? "Submitting..." : "Submit Inquiry"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-[#1a3d18] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <img 
              src={CFI_LOGO} 
              alt="Credit Firm Inc" 
              className="h-10 sm:h-14 w-auto" 
            />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                onClick={() => setShowAddClientModal(true)}
                className="bg-[#84c77f] text-[#1a3d18] hover:bg-[#9ed899] font-medium"
                data-testid="header-quick-add-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Quick Add Client
              </Button>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-[#1a3d18]"
                  data-testid="header-login-btn"
                >
                  Partner Login
                </Button>
              </Link>
              <Link to="/login?type=client">
                <Button 
                  className="bg-white text-[#1a3d18] hover:bg-gray-100 font-medium"
                  data-testid="header-client-login-btn"
                >
                  Client Login
                </Button>
              </Link>
            </div>

            {/* Mobile Navigation - always visible */}
            <div className="flex md:hidden items-center gap-1.5">
              <Button 
                onClick={() => setShowAddClientModal(true)}
                className="bg-[#84c77f] text-[#1a3d18] hover:bg-[#9ed899] font-medium text-xs px-2.5 py-1.5 h-auto"
                data-testid="mobile-quick-add-btn"
              >
                <UserPlus className="h-3.5 w-3.5 mr-1" />
                Add Client
              </Button>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-[#1a3d18] text-xs px-2.5 py-1.5 h-auto"
                  data-testid="mobile-login-btn"
                >
                  Partner
                </Button>
              </Link>
              <Link to="/login?type=client">
                <Button 
                  className="bg-white text-[#1a3d18] hover:bg-gray-100 font-medium text-xs px-2.5 py-1.5 h-auto"
                  data-testid="mobile-client-login-btn"
                >
                  Client
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1a3d18] via-[#265324] to-[#357c2a] text-white">
        {/* Animated background blobs */}
        <motion.div 
          className="absolute inset-0 opacity-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          transition={{ duration: 1 }}
        >
          <motion.div 
            className="absolute top-20 left-10 w-72 h-72 bg-[#84c77f] rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              x: [0, 30, 0],
              y: [0, -20, 0]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 right-10 w-96 h-96 bg-[#357c2a] rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.1, 1],
              x: [0, -20, 0],
              y: [0, 30, 0]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div 
                variants={staggerItem}
                className="inline-flex items-center gap-2 bg-[#84c77f] text-[#1a3d18] px-4 py-2 rounded-full text-sm font-semibold mb-6"
              >
                <Handshake className="h-4 w-4" />
                Partner With Credit Firm Inc
              </motion.div>
              <motion.h1 
                variants={staggerItem}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white"
              >
                Help Your Clients Achieve Their{" "}
                <span className="text-[#84c77f]">Financial Goals</span>
              </motion.h1>
              <motion.p 
                variants={staggerItem}
                className="text-lg md:text-xl text-white/90 mb-8 max-w-xl"
              >
                Partner with Credit Firm Inc to provide your clients with expert credit 
                counseling and repair services. Increase conversions, build loyalty, and 
                grow your business with our proven credit solutions.
              </motion.p>
              <motion.div variants={staggerItem} className="flex flex-wrap gap-4">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <a href="https://partners.creditfirminc.net/register" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg" 
                    className="bg-[#84c77f] text-[#1a3d18] hover:bg-[#9ed899] font-semibold shadow-lg"
                    data-testid="become-partner-btn"
                  >
                    Become a Partner <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                  </a>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <a href="#how-it-works">
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="text-white border-white hover:bg-white hover:text-[#1a3d18] font-semibold"
                      data-testid="learn-more-btn"
                    >
                      Learn More
                    </Button>
                  </a>
                </motion.div>
              </motion.div>
            </motion.div>
            <motion.div 
              className="hidden lg:block"
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            >
              <div className="relative">
                {/* Main hero image - happy couple with new home */}
                <motion.div 
                  className="relative rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_4f2f8805-3336-49fa-9dc7-a44be9a876fa/artifacts/z2lzu476_Happy%20New%20Home%20buyer%20Caucasian%20couples%20from%20Texas%2C%20who%20fixed%20their%20credit%20to%20be%20able%20to%20get%20in%20their%20new%20home.%20one%20is%20in%20a%20green%20shirt..jpg" 
                    alt="Happy couple in their new home"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a3d18]/60 to-transparent"></div>
                  <div className="absolute bottom-4 right-4 text-right">
                    <p className="text-white font-semibold text-lg">Dreams Become Reality</p>
                    <p className="text-white/80 text-sm">Over 20,000 happy clients served</p>
                  </div>
                </motion.div>
                {/* Floating stats card */}
                <motion.div 
                  className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border-l-4 border-[#84c77f]"
                  initial={{ opacity: 0, y: 50, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#84c77f]/20 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-[#357c2a]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1a3d18]">85%</p>
                      <p className="text-sm text-gray-600">Success Rate</p>
                    </div>
                  </div>
                </motion.div>
                {/* Floating testimonial card */}
                <motion.div 
                  className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl p-4 max-w-[200px]"
                  initial={{ opacity: 0, y: -50, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                >
                  <div className="flex gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.2 + i * 0.1 }}
                      >
                        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      </motion.div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 italic">&quot;Credit Firm Inc helped us buy our first home!&quot;</p>
                  <p className="text-xs font-semibold text-[#1a3d18] mt-1">- Sarah M.</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section with Animated Counters */}
      <section className="py-12 bg-[#1a3d18] overflow-hidden">
        <motion.div 
          className="max-w-7xl mx-auto px-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <motion.div variants={staggerItem}>
              <div className="text-4xl font-bold text-[#84c77f]">
                <AnimatedCounter target="20000" suffix="+" />
              </div>
              <p className="text-white/70 text-sm">Happy Clients</p>
            </motion.div>
            <motion.div variants={staggerItem}>
              <div className="text-4xl font-bold text-white">
                <AnimatedCounter target="15" suffix="+" />
              </div>
              <p className="text-white/70 text-sm">Years in Business</p>
            </motion.div>
            <motion.div variants={staggerItem}>
              <div className="text-4xl font-bold text-[#84c77f]">
                <AnimatedCounter target="400" suffix="K+" />
              </div>
              <p className="text-white/70 text-sm">Deletions</p>
            </motion.div>
            <motion.div variants={staggerItem}>
              <div className="text-4xl font-bold text-white">1000s</div>
              <p className="text-white/70 text-sm">New Home Buyers</p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Trust Badges with floating animation */}
      <section className="py-8 bg-white border-y border-gray-100 overflow-hidden">
        <motion.div 
          className="max-w-7xl mx-auto px-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <motion.div 
              variants={staggerItem}
              whileHover={{ scale: 1.1, y: -3 }}
              className="flex items-center gap-2 text-[#265324]"
            >
              <ShieldCheck className="h-6 w-6 text-[#357c2a]" />
              <span className="text-sm font-medium">Licensed & Bonded</span>
            </motion.div>
            <motion.div 
              variants={staggerItem}
              whileHover={{ scale: 1.1, y: -3 }}
              className="flex items-center gap-2 text-[#265324]"
            >
              <BadgeCheck className="h-6 w-6 text-[#84c77f]" />
              <span className="text-sm font-medium">BBB Accredited</span>
            </motion.div>
            <motion.div 
              variants={staggerItem}
              whileHover={{ scale: 1.1, y: -3 }}
              className="flex items-center gap-2 text-[#265324]"
            >
              <Star className="h-6 w-6 text-[#84c77f] fill-[#84c77f]" />
              <span className="text-sm font-medium">5-Star Reviews</span>
            </motion.div>
            <motion.div 
              variants={staggerItem}
              whileHover={{ scale: 1.1, y: -3 }}
              className="flex items-center gap-2 text-[#265324]"
            >
              <Shield className="h-6 w-6 text-[#357c2a]" />
              <span className="text-sm font-medium">Secure & Confidential</span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Success Stories / Testimonials Section */}
      <section className="py-16 md:py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3d18] mb-4">
              Real People, Real Results
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              See how we&apos;ve helped thousands of people achieve their financial dreams.
            </p>
          </AnimatedSection>
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {/* Testimonial 1 */}
            <motion.div 
              variants={staggerItem}
              whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
              className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg transition-shadow"
            >
              <div className="overflow-hidden">
                <motion.img 
                  src="https://customer-assets.emergentagent.com/job_4f2f8805-3336-49fa-9dc7-a44be9a876fa/artifacts/ajgili2l_Husband%20and%20wife%20at%20mortgage%20office%20with%20realtor%20and%20sold%20sign%2C%20one%20is%20wearing%20a%20green%20shirt..jpg" 
                  alt="Happy homeowners with sold sign"
                  className="w-full h-48 object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <div className="p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  &quot;After years of struggling with credit issues, Credit Firm Inc helped us finally qualify for our mortgage. We closed on our dream home last month!&quot;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#84c77f] rounded-full flex items-center justify-center text-[#1a3d18] font-bold">JM</div>
                  <div>
                    <p className="font-semibold text-[#1a3d18]">James & Maria</p>
                    <p className="text-sm text-gray-500">First-Time Homebuyers</p>
                  </div>
                </div>
              </div>
            </motion.div>
            {/* Testimonial 2 */}
            <motion.div 
              variants={staggerItem}
              whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
              className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg transition-shadow"
            >
              <div className="overflow-hidden">
                <motion.img 
                  src="https://customer-assets.emergentagent.com/job_4f2f8805-3336-49fa-9dc7-a44be9a876fa/artifacts/holf3z5x_Husband%20and%20wife%20at%20new%20kitchen%20table%20on%20zoom%20call%20on%20their%20laptop%20with%20credit%20counselor%2C%20one%20is%20in%20a%20green%20shirt..jpg" 
                  alt="Couple on consultation call"
                  className="w-full h-48 object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <div className="p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  &quot;The virtual consultations made it so easy to work with Credit Firm Inc. Our counselor was always available and helped us understand every step.&quot;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#357c2a] rounded-full flex items-center justify-center text-white font-bold">DL</div>
                  <div>
                    <p className="font-semibold text-[#1a3d18]">David & Lisa</p>
                    <p className="text-sm text-gray-500">Credit Recovery Clients</p>
                  </div>
                </div>
              </div>
            </motion.div>
            {/* Testimonial 3 */}
            <motion.div 
              variants={staggerItem}
              whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
              className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg transition-shadow"
            >
              <div className="overflow-hidden">
                <motion.img 
                  src="https://customer-assets.emergentagent.com/job_4f2f8805-3336-49fa-9dc7-a44be9a876fa/artifacts/sptvrlom_Husband%20and%20wife%20at%20mortgage%20office%20with%20credit%20counselor%2C%20one%20is%20in%20a%20green%20shirt..jpg" 
                  alt="Couple meeting with counselor"
                  className="w-full h-48 object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <div className="p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  &quot;Our credit counselor at CFI was incredible. They helped us create a plan that worked for our budget and got us into our new home in just 8 months.&quot;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#84c77f] rounded-full flex items-center justify-center text-[#1a3d18] font-bold">MT</div>
                  <div>
                    <p className="font-semibold text-[#1a3d18]">Mike & Tina</p>
                    <p className="text-sm text-gray-500">New Homeowners</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section id="how-it-works" className="py-16 md:py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3d18] mb-4">
              Why Companies Partner With Credit Firm Inc
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              We help your business succeed by turning credit-challenged prospects into qualified clients.
            </p>
          </AnimatedSection>
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <motion.div whileHover={{ y: -10, scale: 1.02 }} transition={{ duration: 0.3 }}>
                <Card className="bg-white border-0 shadow-lg h-full">
                  <CardHeader>
                    <motion.div 
                      className="w-16 h-16 bg-gradient-to-br from-[#1a3d18] to-[#357c2a] rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                      whileHover={{ rotate: 5, scale: 1.1 }}
                    >
                      <Building2 className="h-8 w-8 text-white" />
                    </motion.div>
                    <CardTitle className="text-xl text-[#1a3d18] font-bold">Home Builders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">
                      Convert more prospects into homebuyers. Our credit services help clients 
                      who aren&apos;t quite loan-ready become qualified buyers for your homes.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
            <motion.div variants={staggerItem}>
              <motion.div whileHover={{ y: -10, scale: 1.02 }} transition={{ duration: 0.3 }}>
                <Card className="bg-white border-0 shadow-lg h-full">
                  <CardHeader>
                    <motion.div 
                      className="w-16 h-16 bg-gradient-to-br from-[#1a3d18] to-[#357c2a] rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                      whileHover={{ rotate: 5, scale: 1.1 }}
                    >
                      <Home className="h-8 w-8 text-white" />
                    </motion.div>
                    <CardTitle className="text-xl text-[#1a3d18] font-bold">Real Estate Agents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">
                      Don&apos;t lose leads to poor credit. Refer clients to us and we&apos;ll work to 
                      get them loan-ready, then send them back to you when they qualify.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
            <motion.div variants={staggerItem}>
              <motion.div whileHover={{ y: -10, scale: 1.02 }} transition={{ duration: 0.3 }}>
                <Card className="bg-white border-0 shadow-lg h-full">
                  <CardHeader>
                    <motion.div 
                      className="w-16 h-16 bg-gradient-to-br from-[#1a3d18] to-[#357c2a] rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                      whileHover={{ rotate: 5, scale: 1.1 }}
                    >
                      <Briefcase className="h-8 w-8 text-white" />
                    </motion.div>
                    <CardTitle className="text-xl text-[#1a3d18] font-bold">Lenders & Financial Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">
                      Expand your qualified applicant pool. We prepare clients for successful 
                      loan applications, reducing your denial rates and saving time.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How Partnership Works */}
      <section className="py-16 md:py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3d18] mb-4">
              How Our Partnership Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              A simple, streamlined process that benefits you and your clients.
            </p>
          </AnimatedSection>
          <motion.div 
            className="grid md:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem} className="text-center">
              <motion.div 
                className="w-16 h-16 bg-gradient-to-br from-[#1a3d18] to-[#357c2a] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white shadow-lg"
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.5 }}
              >1</motion.div>
              <h3 className="font-bold text-[#1a3d18] mb-2 text-lg">Refer Clients</h3>
              <p className="text-gray-600">Send us clients who need credit help through our easy partner portal</p>
            </motion.div>
            <motion.div variants={staggerItem} className="text-center">
              <motion.div 
                className="w-16 h-16 bg-gradient-to-br from-[#1a3d18] to-[#357c2a] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white shadow-lg"
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.5 }}
              >2</motion.div>
              <h3 className="font-bold text-[#1a3d18] mb-2 text-lg">We Get to Work</h3>
              <p className="text-gray-600">Our experts create personalized plans and guide clients through credit improvement</p>
            </motion.div>
            <motion.div variants={staggerItem} className="text-center">
              <motion.div 
                className="w-16 h-16 bg-gradient-to-br from-[#1a3d18] to-[#357c2a] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white shadow-lg"
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.5 }}
              >3</motion.div>
              <h3 className="font-bold text-[#1a3d18] mb-2 text-lg">Track Progress</h3>
              <p className="text-gray-600">Monitor client progress in real-time through your dedicated dashboard</p>
            </motion.div>
            <motion.div variants={staggerItem} className="text-center">
              <motion.div 
                className="w-16 h-16 bg-[#84c77f] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                whileHover={{ scale: 1.2 }}
                transition={{ duration: 0.3 }}
              >
                <CheckCircle2 className="h-8 w-8 text-[#1a3d18]" />
              </motion.div>
              <h3 className="font-bold text-[#1a3d18] mb-2 text-lg">Close the Deal</h3>
              <p className="text-gray-600">When clients are loan-ready, they come back to you to complete the transaction</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 md:py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3d18] mb-6">
                Partner Benefits
              </h2>
              <p className="text-gray-700 mb-8 text-lg">
                When you partner with Credit Firm Inc, you get more than a referral program. 
                You get a comprehensive solution that helps you serve more clients and grow your business.
              </p>
              <motion.div 
                className="space-y-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                <motion.div variants={staggerItem} className="flex items-start gap-4">
                  <motion.div 
                    className="w-8 h-8 bg-[#84c77f] rounded-full flex items-center justify-center flex-shrink-0"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#1a3d18]" />
                  </motion.div>
                  <div>
                    <p className="font-bold text-[#1a3d18]">Dedicated Partner Portal</p>
                    <p className="text-gray-600">Easy-to-use dashboard to manage referrals and track client progress</p>
                  </div>
                </motion.div>
                <motion.div variants={staggerItem} className="flex items-start gap-4">
                  <motion.div 
                    className="w-8 h-8 bg-[#84c77f] rounded-full flex items-center justify-center flex-shrink-0"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#1a3d18]" />
                  </motion.div>
                  <div>
                    <p className="font-bold text-[#1a3d18]">Co-Branded Materials</p>
                    <p className="text-gray-600">Marketing materials and resources to promote our partnership</p>
                  </div>
                </motion.div>
                <motion.div variants={staggerItem} className="flex items-start gap-4">
                  <motion.div 
                    className="w-8 h-8 bg-[#84c77f] rounded-full flex items-center justify-center flex-shrink-0"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#1a3d18]" />
                  </motion.div>
                  <div>
                    <p className="font-bold text-[#1a3d18]">Priority Support</p>
                    <p className="text-gray-600">Dedicated account manager and priority client processing</p>
                  </div>
                </motion.div>
                <motion.div variants={staggerItem} className="flex items-start gap-4">
                  <motion.div 
                    className="w-8 h-8 bg-[#84c77f] rounded-full flex items-center justify-center flex-shrink-0"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#1a3d18]" />
                  </motion.div>
                  <div>
                    <p className="font-bold text-[#1a3d18]">Real-Time Notifications</p>
                    <p className="text-gray-600">Get notified when clients reach key milestones or become loan-ready</p>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
            <motion.div 
              className="relative"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              {/* Main consultation image */}
              <motion.div 
                className="rounded-2xl overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_4f2f8805-3336-49fa-9dc7-a44be9a876fa/artifacts/tczeaiga_Happy%20New%20Home%20buyer%20Caucasian%20couples%20from%20Texas%2C%20who%20fixed%20their%20credit%20to%20be%20able%20to%20get%20in%20their%20new%20home.%20one%20is%20in%20a%20green%20shirt.%20%281%29.jpg" 
                  alt="Happy homeowners celebrating"
                  className="w-full h-[350px] object-cover"
                />
              </motion.div>
              {/* Stats overlay cards */}
              <div className="absolute -bottom-6 left-4 right-4 grid grid-cols-2 gap-3">
                <motion.div 
                  className="bg-gradient-to-br from-[#1a3d18] to-[#357c2a] text-white rounded-xl p-4 shadow-xl"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ y: -5, scale: 1.05 }}
                >
                  <TrendingUp className="h-6 w-6 text-[#84c77f] mb-1" />
                  <p className="text-2xl font-bold">40%</p>
                  <p className="text-xs text-white/80">Conversion Increase</p>
                </motion.div>
                <motion.div 
                  className="bg-white rounded-xl p-4 shadow-xl"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ y: -5, scale: 1.05 }}
                >
                  <Award className="h-6 w-6 text-[#357c2a] mb-1" />
                  <p className="text-2xl font-bold text-[#1a3d18]">85%</p>
                  <p className="text-xs text-gray-600">Success Rate</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Referral Section - Chat Style */}
      <section id="client-form" className="py-16 md:py-24 bg-gradient-to-br from-[#1a3d18] to-[#357c2a]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Are You Looking to Improve Your Credit?
            </h2>
            <p className="text-white/80 text-lg">
              Let&apos;s chat! Answer a few questions and our team will reach out 
              to start your personalized journey to better credit.
            </p>
          </div>

          <CFIConversationalSurvey onComplete={handleSurveyComplete} />
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#265324] mb-4">
              What Our Clients Say
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real reviews from people who&apos;ve worked with Credit Firm Inc to achieve their financial goals.
            </p>
          </div>
          <CFIReviewWidget />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#265324] mb-4">
            Ready to Partner With Us?
          </h2>
          <p className="text-gray-600 mb-8 text-lg">
            Join our growing network of partners and start helping your clients achieve their financial goals today.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://partners.creditfirminc.net/register" target="_blank" rel="noopener noreferrer">
            <Button 
              size="lg"
              className="bg-[#357c2a] text-white hover:bg-[#265324] font-semibold"
            >
              Become a Partner <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            </a>
            <a href="#client-form">
              <Button 
                size="lg" 
                variant="outline"
                className="border-[#357c2a] text-[#357c2a] hover:bg-[#357c2a] hover:text-white"
              >
                I Need Credit Help
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-[#265324] to-[#1a3d18] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <img src={CFI_LOGO} alt="Credit Firm Inc" className="h-12 w-auto mb-4" />
              <p className="text-white/60 text-sm max-w-md">
                Credit Firm Inc is a trusted credit counseling and repair service helping 
                individuals and businesses achieve their financial goals through expert 
                guidance and proven strategies.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#84c77f] mb-4">Quick Links</h4>
              <div className="space-y-2">
                <a href="#how-it-works" className="block text-white/60 hover:text-[#84c77f] text-sm transition-colors">How It Works</a>
                <a href="#client-form" className="block text-white/60 hover:text-[#84c77f] text-sm transition-colors">Get Started</a>
                <Link to="/login" className="block text-white/60 hover:text-[#84c77f] text-sm transition-colors">Partner Login</Link>
                <Link to="/login?type=client" className="block text-white/60 hover:text-[#84c77f] text-sm transition-colors">Client Login</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-[#84c77f] mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-white/60">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href="tel:432-599-4880" className="hover:text-[#84c77f] transition-colors">(432) 599-4880</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:partners@creditfirminc.com" className="hover:text-[#84c77f] transition-colors">partners@creditfirminc.com</a>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <a href="https://creditfirminc.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#84c77f] transition-colors">See Locations</a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Credit Firm Inc. All rights reserved.
            </p>
            <div className="flex gap-4">
              <a href="https://partners.creditfirminc.net/refer" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#84c77f] text-sm transition-colors">
                Betenbough Partner Site
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CFILandingPage;
